import * as THREE from 'three'
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader.js'
import { readFileBytes } from '@core/lib/readFile'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  type Caixa,
  distanciaEntreCaixas,
  matrizDaNuvem,
} from './colocacao'
import {
  ALVO_POR_OMISSAO,
  type Progresso,
  contagemDeclarada,
  inicioDaCor,
  lerNuvemEmFluxo,
  lerPonto,
  origemPara,
  resumoDaLeitura,
} from './nuvemEmFluxo'

/** Formatos de nuvem de pontos que a plataforma abre, para o selector
 *  de ficheiros e para as mensagens de erro dizerem o mesmo. */
export const POINT_CLOUD_EXTENSIONS = [
  '.las',
  '.laz',
  '.e57',
  '.ply',
  '.xyz',
  '.pts',
] as const

export const POINT_CLOUD_ACCEPT = POINT_CLOUD_EXTENSIONS.join(',')

export interface PointCloudEntry {
  id: string
  filename: string
  pointCount: number
  object: THREE.Points
  /** Quantos pontos o ficheiro tinha, quando se guardou só uma parte.
   *  Null quando se mostra tudo. Existe para o painel poder dizer que
   *  reduziu, em vez de a pessoa pensar que está a ver o levantamento
   *  inteiro. */
  pontosNoFicheiro: number | null
  /** O que dizer sobre a leitura. */
  resumo: string | null
  /** O ficheiro de onde veio, para se poder voltar a ler com outra
   *  densidade sem o escolher outra vez. O browser mantém a referência
   *  enquanto a página estiver aberta; não se guarda o conteúdo. */
  ficheiro: File
  /** A densidade com que foi lida. */
  alvo: number
}

const clouds = new Map<string, PointCloudEntry>()
const listeners = new Set<() => void>()
let snapshot: PointCloudEntry[] = []

/** A carga que está a decorrer, se houver.
 *
 *  Vive aqui e não no painel: uma nuvem pode entrar pelo painel, pela
 *  caixa inicial, pelo Abrir modelo ou arrastada para a janela, e o
 *  progresso de um PTS de 15 GB tem de se ver venha ela por onde vier.
 *  Quando vivia no painel, só a carga feita a partir do painel tinha
 *  barra; as outras eram três minutos a olhar para nada. */
export interface CargaEmCurso {
  nome: string
  progresso: Progresso | null
  erro: string | null
}
let emCurso: CargaEmCurso | null = null
let ultimoErro: string | null = null
let cancelador: AbortController | null = null

function notify() {
  snapshot = [...clouds.values()]
  for (const listener of listeners) listener()
}

/** Onde começa a cor, numa linha com este número de colunas.
 *
 *  Vive em `nuvemEmFluxo.ts` e é o mesmo nos dois leitores: um PTS do
 *  Revit tem 10 colunas (x y z i r g b nx ny nz) e a cor continua na 4.
 *  Este nome fica porque os testes e o leitor de E57 já o conhecem. */
export const colorOffset = inicioDaCor

/**
 * Lê um XYZ ou PTS que já está todo em memória.
 *
 * Só para o que é pequeno: o leitor de E57 produz o texto aqui, e esse
 * nunca passa dos 80 MB. Um ficheiro escolhido pela pessoa vai pelo
 * `lerNuvemEmFluxo`, que não pede o ficheiro inteiro e aguenta os 16 GB
 * de um levantamento completo. As duas leituras usam a mesma regra de
 * linha, `lerPonto`: dois leitores com regras próprias era o sítio onde
 * um corrigia um defeito e o outro ficava com ele.
 */
export function parseXyz(text: string): THREE.BufferGeometry {
  const positions: number[] = []
  // A cor fica em bruto: só no fim se sabe se o ficheiro escreve os
  // canais de 0 a 255 ou de 0 a 1. Decidir linha a linha fazia um ponto
  // escuro (0 0 1 em 0..255) passar por 0..1 e sair azul vivo.
  const raw: number[] = []
  let hasColors = false
  let maxChannel = 0
  const lines = text.split(/\r?\n/)
  // Origem local em coordenadas grandes, como no leitor em fluxo: a
  // subtracção é em float64, antes do float32 (ver origemPara).
  let origem: [number, number, number] | null = null
  for (let index = 0; index < lines.length; index += 1) {
    if (index === 0 && contagemDeclarada(lines[index]) !== null) continue
    const pt = lerPonto(lines[index])
    if (!pt) continue
    origem ??= origemPara(pt)
    positions.push(pt.x - origem[0], pt.y - origem[1], pt.z - origem[2])
    if (pt.cor) {
      raw.push(pt.cor[0], pt.cor[1], pt.cor[2])
      maxChannel = Math.max(maxChannel, pt.cor[0], pt.cor[1], pt.cor[2])
      hasColors = true
    } else raw.push(-1, -1, -1)
  }
  if (positions.length === 0) throw new Error('O ficheiro não contém pontos XYZ válidos.')
  const geometry = new THREE.BufferGeometry()
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
  if (hasColors) {
    const scale = maxChannel > 1 ? 255 : 1
    const colors = raw.map((canal, i) =>
      canal < 0 ? [0.72, 0.78, 0.86][i % 3] : canal / scale,
    )
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3))
  }
  geometry.computeBoundingBox()
  guardarOrigem(geometry, origem ?? [0, 0, 0])
  return geometry
}

/** A origem local de uma geometria de nuvem (coordenadas do ficheiro).
 *  As posições da geometria são relativas a ela; o objecto 3D leva-a na
 *  matriz (ver `colocar`). */
export function origemDaGeometria(g: THREE.BufferGeometry): [number, number, number] {
  const o = g.userData.vn2rOrigem as [number, number, number] | undefined
  return Array.isArray(o) && o.length === 3 ? o : [0, 0, 0]
}

function guardarOrigem(g: THREE.BufferGeometry, origem: [number, number, number]): void {
  g.userData.vn2rOrigem = origem
}

/** Posições x y z seguidas (em float64) para float32 relativas à origem
 *  local do primeiro ponto (ver origemPara). */
export function posicoesRelativas(valores: ArrayLike<number>): {
  posicoes: Float32Array
  origem: [number, number, number]
} {
  const origem = origemPara({ x: valores[0], y: valores[1], z: valores[2] })
  const posicoes = new Float32Array(valores.length)
  for (let i = 0; i < valores.length; i += 3) {
    posicoes[i] = valores[i] - origem[0]
    posicoes[i + 1] = valores[i + 1] - origem[1]
    posicoes[i + 2] = valores[i + 2] - origem[2]
  }
  return { posicoes, origem }
}

/** Versão declarada no cabeçalho de um LAS ou LAZ, ou null se aquilo
 *  não for um ficheiro LAS.
 *
 *  O cabeçalho é comum aos dois: a assinatura "LASF" nos primeiros
 *  quatro bytes, e a versão em dois bytes no offset 24. Ler isto antes
 *  de chamar o leitor permite recusar com uma frase que se entende, em
 *  vez de deixar sair "Only file versions <= 1.3 are supported at this
 *  time" de dentro da biblioteca. */
export function lasVersion(bytes: ArrayBuffer): string | null {
  if (bytes.byteLength < 26) return null
  const b = new Uint8Array(bytes, 0, 26)
  const assinatura = String.fromCharCode(b[0], b[1], b[2], b[3])
  if (assinatura !== 'LASF') return null
  return `${b[24]}.${b[25]}`
}

/** A versão mais recente que o leitor abre. O LAS 1.4 tem um cabeçalho
 *  e registos de ponto diferentes, e o descompressor que usamos ainda
 *  não os lê. */
export const LAS_VERSAO_MAXIMA = '1.3'

export function lasVersionSuportada(versao: string | null): boolean {
  if (!versao) return false
  const [maior, menor] = versao.split('.').map(Number)
  if (!Number.isFinite(maior) || !Number.isFinite(menor)) return false
  const [maxMaior, maxMenor] = LAS_VERSAO_MAXIMA.split('.').map(Number)
  return maior < maxMaior || (maior === maxMaior && menor <= maxMenor)
}

/** Lê LAS e LAZ.
 *
 *  Import dinâmico de propósito: o leitor traz o descompressor do LAZ em
 *  WebAssembly e são vários MB. Quem nunca abre uma nuvem não os deve
 *  pagar, tal como já acontece com o mapa e com a revisão de PDF.
 *
 *  `worker: false` não é detalhe: o loader, no modo worker, vai buscar o
 *  bundle ao unpkg.com. A regra da casa é que nada em tempo de execução
 *  sai para um CDN, e os nossos cabeçalhos COOP/COEP bloqueavam-no de
 *  qualquer maneira. Fica tudo no fio principal. */
async function geometryFromLas(file: File): Promise<THREE.BufferGeometry> {
  const bytes = await readFileBytes(file)
  const versao = lasVersion(bytes)
  if (!lasVersionSuportada(versao)) {
    throw new Error(
      versao
        ? `Este ficheiro é LAS ${versao}, e o leitor vai até à ` +
          `${LAS_VERSAO_MAXIMA}. Volta a exportá-lo em LAS 1.2, ou ` +
          `converte-o (por exemplo no CloudCompare), e abre outra vez.`
        : 'Isto não parece um ficheiro LAS ou LAZ: falta a assinatura ' +
          'LASF no início.',
    )
  }
  const [{ parse }, { LASLoader }] = await Promise.all([
    import('@loaders.gl/core'),
    import('@loaders.gl/las'),
  ])
  const malha = await parse(bytes, LASLoader, {
    worker: false,
    // 'auto' e nao um valor fixo: a norma diz que a cor do LAS e de 16
    // bits, mas muitos exportadores escrevem valores de 0 a 255 nesses
    // campos. Com um valor fixo, metade dos ficheiros sai com a cor
    // errada; o 'auto' olha para os pontos e decide. E a mesma escolha
    // que o nosso leitor de XYZ ja fazia.
    // fp64: as coordenadas chegam em float64. Em float32 (o que o leitor
    // dá por omissão) um LAS em UTM já vinha arredondado a 0,5 m antes de
    // chegar aqui; a origem local só resolve se a subtracção for antes.
    las: { shape: 'mesh', colorDepth: 'auto', fp64: true },
  })
  const atributos = malha.attributes as Record<
    string,
    { value: ArrayLike<number>; size: number } | undefined
  >
  const posicao = atributos.POSITION
  if (!posicao || posicao.value.length === 0) {
    throw new Error('O ficheiro não contém pontos.')
  }

  const geometry = new THREE.BufferGeometry()
  const relativas = posicoesRelativas(posicao.value)
  geometry.setAttribute('position', new THREE.BufferAttribute(relativas.posicoes, 3))
  guardarOrigem(geometry, relativas.origem)

  // A cor do LAS vem em canais de 8 ou 16 bits; pedimos 8 e aqui passa
  // para 0..1, que é o que o three espera. Um LAS sem cor não traz o
  // atributo, e nesse caso a nuvem fica com a cor por omissão.
  const cor = atributos.COLOR_0
  if (cor && cor.value.length > 0) {
    const canais = cor.size
    const total = cor.value.length / canais
    const rgb = new Float32Array(total * 3)
    for (let i = 0; i < total; i += 1) {
      // O leitor devolve sempre RGBA de 8 bits, ja normalizado a partir
      // do que o ficheiro tinha. Fica a faltar passar para 0..1.
      rgb[i * 3] = cor.value[i * canais] / 255
      rgb[i * 3 + 1] = cor.value[i * canais + 1] / 255
      rgb[i * 3 + 2] = cor.value[i * canais + 2] / 255
    }
    geometry.setAttribute('color', new THREE.Float32BufferAttribute(rgb, 3))
  }

  geometry.computeBoundingBox()
  return geometry
}

interface Lida {
  geometry: THREE.BufferGeometry
  pontosNoFicheiro: number | null
  resumo: string | null
}

export interface OpcoesDeCarga {
  aoProgredir?: (p: Progresso) => void
  sinal?: AbortSignal
  /** Quantos pontos guardar, nos formatos que se lêem aos pedaços. */
  alvo?: number
}

async function geometryFrom(
  file: File,
  opcoes: OpcoesDeCarga,
): Promise<Lida> {
  if (/\.(xyz|pts)$/i.test(file.name)) {
    // Pelo fluxo, sempre. O `file.text()` pedia o ficheiro inteiro como
    // uma cadeia de texto, e um PTS de 16 GB não cabe em cadeia
    // nenhuma: o Chrome recusava e nós dizíamos que era o OneDrive.
    const r = await lerNuvemEmFluxo(file, {
      alvo: opcoes.alvo,
      aoProgredir: opcoes.aoProgredir,
      sinal: opcoes.sinal,
    })
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.BufferAttribute(r.posicoes, 3))
    // `true` no fim: a GPU lê os bytes como 0..1. Sem isso lia 0..255
    // como se fossem cores já normalizadas, e a nuvem saía toda branca.
    if (r.cores) geometry.setAttribute('color', new THREE.BufferAttribute(r.cores, 3, true))
    geometry.computeBoundingBox()
    guardarOrigem(geometry, r.origem)
    return {
      geometry,
      pontosNoFicheiro: r.passo > 1 ? r.lidos : null,
      resumo: resumoDaLeitura(r),
    }
  }
  return { geometry: await geometriaDeOutros(file), pontosNoFicheiro: null, resumo: null }
}

async function geometriaDeOutros(file: File): Promise<THREE.BufferGeometry> {
  if (/\.(las|laz)$/i.test(file.name)) return await geometryFromLas(file)
  if (/\.e57$/i.test(file.name)) {
    // Import dinâmico: o leitor de E57 traz 283 kB de WebAssembly e só
    // se carrega a quem abre mesmo um E57.
    const { geometryFromE57 } = await import('./e57')
    return await geometryFromE57(await readFileBytes(file))
  }
  if (/\.ply$/i.test(file.name)) return new PLYLoader().parse(await readFileBytes(file))
  throw new Error(
    `Formato não suportado. Usa ${POINT_CLOUD_EXTENSIONS.join(', ')}.`,
  )
}

/** Põe a nuvem na matriz dada, por colunas.
 *
 *  Desfaz a matriz em posição, rotação e escala em vez de a escrever no
 *  `matrix` com o `matrixAutoUpdate` desligado: assim o `setPosition`
 *  que já existe, o cálculo de desvios e o resto do three.js continuam a
 *  funcionar sem saberem que isto aconteceu.
 *
 *  A geometria tem as posições relativas à sua origem local (ver
 *  origemDaGeometria), por isso a matriz final é `m × T(origem)`: o
 *  ponto do ficheiro `p` vai parar a `m · p`, como antes. A colocação
 *  pedida fica em `userData.vn2rColocacao`, para se poder voltar a
 *  aplicar quando a geometria muda (outra densidade). */
function colocar(objeto: THREE.Points, m: readonly number[]): void {
  const [ox, oy, oz] = origemDaGeometria(objeto.geometry)
  const matriz = new THREE.Matrix4().fromArray([...m])
  // Sem origem local (coordenadas pequenas) a matriz é exactamente a de
  // antes, bit a bit.
  if (ox !== 0 || oy !== 0 || oz !== 0) {
    matriz.multiply(new THREE.Matrix4().makeTranslation(ox, oy, oz))
  }
  matriz.decompose(objeto.position, objeto.quaternion, objeto.scale)
  objeto.userData.vn2rColocacao = [...m]
  objeto.updateMatrixWorld(true)
}

function caixaNoMundo(objeto: THREE.Object3D): Caixa | null {
  objeto.updateMatrixWorld(true)
  const b = new THREE.Box3().setFromObject(objeto)
  if (b.isEmpty()) return null
  return { min: b.min.toArray() as [number, number, number], max: b.max.toArray() as [number, number, number] }
}

function caixaDoModelo(modelId: string): Caixa | null {
  const m = viewerApi.getModel(modelId) as { box?: THREE.Box3 } | null | undefined
  const b = m?.box
  if (!b || b.isEmpty()) return null
  return { min: b.min.toArray() as [number, number, number], max: b.max.toArray() as [number, number, number] }
}

const CHAVE_DENSIDADE = 'vn2r.nuvem.densidade'

/** A última densidade escolhida neste browser. É uma comodidade de quem
 *  usa, e não um dado: se o armazenamento falhar, volta ao valor por
 *  omissão e tudo funciona na mesma. */
export function densidadePreferida(): number {
  try {
    const v = Number(localStorage.getItem(CHAVE_DENSIDADE))
    return Number.isFinite(v) && v >= 100_000 ? v : ALVO_POR_OMISSAO
  } catch {
    return ALVO_POR_OMISSAO
  }
}

export function guardarDensidade(alvo: number): void {
  try {
    localStorage.setItem(CHAVE_DENSIDADE, String(alvo))
  } catch {
    /* sem onde guardar: a próxima vez usa o valor por omissão */
  }
}

export const pointCloudApi = {
  subscribe(listener: () => void): () => void {
    listeners.add(listener)
    return () => listeners.delete(listener)
  },

  list(): PointCloudEntry[] {
    return snapshot
  },

  /** A carga a decorrer, para quem a quiser mostrar. */
  cargaEmCurso(): CargaEmCurso | null {
    return emCurso
  },

  /** O erro da última carga, até alguém o limpar ou começar outra. */
  ultimoErro(): string | null {
    return ultimoErro
  },

  limparErro(): void {
    if (ultimoErro === null) return
    ultimoErro = null
    notify()
  },

  cancelar(): void {
    cancelador?.abort()
  },

  /**
   * Abre uma nuvem venha de onde vier, com progresso e com erro
   * guardados na api.
   *
   * É a porta única para quem não é o painel: a caixa inicial, o Abrir
   * modelo, o arrastar para a janela. Devolve null quando falhou ou foi
   * cancelada; o motivo fica em `ultimoErro`, onde o painel o mostra.
   */
  async abrir(file: File, alvo?: number): Promise<PointCloudEntry | null> {
    cancelador?.abort()
    const controlo = new AbortController()
    cancelador = controlo
    ultimoErro = null
    emCurso = { nome: file.name, progresso: null, erro: null }
    notify()
    try {
      return await pointCloudApi.load(file, {
        alvo: alvo ?? densidadePreferida(),
        sinal: controlo.signal,
        aoProgredir: (p) => {
          if (emCurso) emCurso = { ...emCurso, progresso: p }
          notify()
        },
      })
    } catch (causa) {
      // Cancelar não é um erro: foi a pessoa que pediu.
      if (!(causa instanceof DOMException && causa.name === 'AbortError')) {
        ultimoErro = causa instanceof Error ? causa.message : String(causa)
      }
      return null
    } finally {
      if (cancelador === controlo) cancelador = null
      emCurso = null
      notify()
    }
  },

  async load(file: File, opcoes: OpcoesDeCarga = {}): Promise<PointCloudEntry> {
    const world = viewerApi.getWorld()
    if (!world) throw new Error('O viewer ainda não está pronto.')
    const { geometry, pontosNoFicheiro, resumo } = await geometryFrom(
      file,
      opcoes,
    )
    const colors = geometry.getAttribute('color')
    const material = new THREE.PointsMaterial({
      size: 0.035,
      sizeAttenuation: true,
      color: colors ? 0xffffff : 0xb8c6dc,
      vertexColors: Boolean(colors),
    })
    const object = new THREE.Points(geometry, material)
    object.name = `vn2r-point-cloud:${file.name}`
    // De pé: os levantamentos têm o Z para cima e o 3D tem o Y. Sem
    // isto, todas as nuvens entravam deitadas de lado, em todos os
    // formatos. Ver colocacao.ts.
    colocar(object, matrizDaNuvem(null))
    const entry: PointCloudEntry = {
      id: crypto.randomUUID(),
      filename: file.name,
      pointCount: geometry.getAttribute('position').count,
      object,
      pontosNoFicheiro,
      resumo,
      ficheiro: file,
      alvo: opcoes.alvo ?? ALVO_POR_OMISSAO,
    }
    clouds.set(entry.id, entry)
    world.scene.three.add(object)
    // Sem mais nada aberto, a câmara vai para a nuvem. Ficava na vista
    // inicial, apontada à origem, e uma nuvem que não estivesse ao pé da
    // origem não se via: parecia que abrir uma nuvem sozinha não
    // funcionava. Com um modelo aberto não se mexe na vista de quem a
    // escolheu; aí é o aviso de distância que ajuda.
    const haModelos = [...viewerApi.iterModels()].length > 0
    if (!haModelos && clouds.size === 1) pointCloudApi.enquadrar(entry.id)
    viewerApi._invalidate()
    notify()
    return entry
  },

  setPointSize(id: string, size: number): void {
    const material = clouds.get(id)?.object.material as THREE.PointsMaterial | undefined
    if (!material) return
    material.size = Math.max(0.001, Math.min(size, 5))
    material.needsUpdate = true
    viewerApi._invalidate()
  },

  setPosition(id: string, x: number, y: number, z: number): void {
    const object = clouds.get(id)?.object
    if (!object) return
    object.position.set(x, y, z)
    object.updateMatrixWorld(true)
    viewerApi._invalidate()
  },

  /**
   * Põe a nuvem no mesmo sítio do modelo.
   *
   * Assume que a nuvem foi exportada nas coordenadas do projecto, que é
   * o que acontece quando as duas vêm do mesmo Revit. Não é verdade
   * sempre, e por isso isto é um botão e não uma coisa automática.
   */
  alinharAoModelo(cloudId: string, modelId: string): void {
    const entrada = clouds.get(cloudId)
    const modelo = viewerApi.getModel(modelId) as
      | { object?: THREE.Object3D }
      | null
      | undefined
    const objectoDoModelo = modelo?.object
    if (!entrada || !objectoDoModelo) return
    objectoDoModelo.updateMatrixWorld(true)
    colocar(entrada.object, matrizDaNuvem(objectoDoModelo.matrixWorld.toArray()))
    viewerApi._invalidate()
    notify()
  },

  /** Quão longe a nuvem está do modelo, em metros. Zero quando se
   *  tocam; null quando não há como medir. */
  distanciaAoModelo(cloudId: string, modelId: string): number | null {
    const entrada = clouds.get(cloudId)
    if (!entrada) return null
    const nuvem = caixaNoMundo(entrada.object)
    const modelo = caixaDoModelo(modelId)
    if (!nuvem || !modelo) return null
    return distanciaEntreCaixas(nuvem, modelo)
  },

  /**
   * Volta a ler a nuvem com outra densidade, no mesmo sítio.
   *
   * Troca só a geometria do objecto que já está na cena. A posição, a
   * rotação, o alinhamento ao IFC e o tamanho dos pontos ficam: quem
   * alinhou a nuvem e depois pediu mais pontos não quer ter de a
   * alinhar outra vez.
   */
  async mudarDensidade(cloudId: string, alvo: number): Promise<void> {
    const entrada = clouds.get(cloudId)
    if (!entrada) return
    cancelador?.abort()
    const controlo = new AbortController()
    cancelador = controlo
    ultimoErro = null
    emCurso = { nome: entrada.filename, progresso: null, erro: null }
    notify()
    try {
      const lida = await geometryFrom(entrada.ficheiro, {
        alvo,
        sinal: controlo.signal,
        aoProgredir: (p) => {
          if (emCurso) emCurso = { ...emCurso, progresso: p }
          notify()
        },
      })
      const antiga = entrada.object.geometry
      entrada.object.geometry = lida.geometry
      antiga.dispose()
      // A geometria nova pode ter outra origem local: volta-se a aplicar a
      // mesma colocação (de pé, ou alinhada ao IFC) com a origem nova.
      const colocacao = entrada.object.userData.vn2rColocacao as number[] | undefined
      if (colocacao) colocar(entrada.object, colocacao)
      const material = entrada.object.material as THREE.PointsMaterial
      material.vertexColors = Boolean(lida.geometry.getAttribute('color'))
      material.needsUpdate = true
      entrada.pointCount = lida.geometry.getAttribute('position').count
      entrada.pontosNoFicheiro = lida.pontosNoFicheiro
      entrada.resumo = lida.resumo
      entrada.alvo = alvo
      guardarDensidade(alvo)
      viewerApi._invalidate()
    } catch (causa) {
      if (!(causa instanceof DOMException && causa.name === 'AbortError')) {
        ultimoErro = causa instanceof Error ? causa.message : String(causa)
      }
    } finally {
      if (cancelador === controlo) cancelador = null
      emCurso = null
      notify()
    }
  },

  /** Enquadra a câmara na nuvem. */
  enquadrar(cloudId: string): void {
    const entrada = clouds.get(cloudId)
    const mundo = viewerApi.getWorld()
    if (!entrada || !mundo) return
    entrada.object.updateMatrixWorld(true)
    const caixa = new THREE.Box3().setFromObject(entrada.object)
    if (caixa.isEmpty()) return
    void mundo.camera.controls?.fitToBox(caixa, true)
    viewerApi._invalidate()
  },

  remove(id: string): void {
    const entry = clouds.get(id)
    if (!entry) return
    entry.object.removeFromParent()
    entry.object.geometry.dispose()
    const materials = Array.isArray(entry.object.material)
      ? entry.object.material
      : [entry.object.material]
    for (const material of materials) material.dispose()
    clouds.delete(id)
    viewerApi._invalidate()
    notify()
  },

  clear(): void {
    for (const id of [...clouds.keys()]) pointCloudApi.remove(id)
  },
}
